import React from 'react';

import { Filter } from '@models/feature-descriptor';
import { TableColumns } from '@models/column';

import {
  FeatureDescriptorEditorActions,
  useFeatureDescriptorEditorContext,
} from './context';
import { FilterDraft } from './models';
import {
  FilterTypeSelector,
  EditorWrapper,
  DraftEditorProps,
  TableColumnEditor,
  NumberEditor,
  StringEditor,
  ChipEditor,
} from './components';

const FilterEditorTemplate: Record<
  Filter.Type,
  (props: DraftEditorProps<FilterDraft>) => JSX.Element[]
> = {
  [Filter.Type.AndFilter]: props => {
    return [
      <div style={{ display: 'flex', flexFlow: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'start' }}>
          <span style={{ lineHeight: '26px' }}>filter1= </span>
          <InlineFilterEditor
            {...props}
            filter={props.draft?.filter1}
            onFilterChange={filter1 =>
              props.onDraftChange({ ...props.draft, filter1 })
            }
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'start' }}>
          <span style={{ lineHeight: '26px' }}>filter2= </span>
          <InlineFilterEditor
            {...props}
            filter={props.draft?.filter2}
            onFilterChange={filter2 =>
              props.onDraftChange({ ...props.draft, filter2 })
            }
          />
        </div>
      </div>,
    ];
  },
  [Filter.Type.Above]: props => [
    <TableColumnEditor label="column" {...props} />,
    <NumberEditor label="min_inclusive" {...props} />,
  ],
  [Filter.Type.Below]: props => [
    <TableColumnEditor label="column" {...props} />,
    <NumberEditor label="max_exclusive" {...props} />,
  ],
  [Filter.Type.Between]: props => [
    <TableColumnEditor label="column" {...props} />,
    <NumberEditor label="min_inclusive" {...props} />,
    <NumberEditor label="max_exclusive" {...props} />,
  ],
  [Filter.Type.FullPass]: () => [],
  [Filter.Type.HourOfDay]: props => {
    return [
      <TableColumnEditor label="column" {...props} />,
      <NumberEditor label="min_hour_inclusive" {...props} />,
      <NumberEditor label="max_hour_exclusive" {...props} />,
    ];
  },
  [Filter.Type.NgramFilter]: props => [
    <TableColumnEditor label="column" {...props} />,
    <ChipEditor label="ngram" {...props} />,
  ],
  [Filter.Type.Value]: props => [
    <TableColumnEditor label="column" {...props} />,
    <StringEditor label="value" {...props} />,
  ],
  [Filter.Type.Weekday]: props => {
    return [<TableColumnEditor label="column" {...props} />];
  },
  [Filter.Type.Weekend]: props => {
    return [<TableColumnEditor label="column" {...props} />];
  },
};

interface InlineFilterEditor {
  tableColumns: TableColumns;
  filter: FilterDraft;
  onFilterChange: (filter: FilterDraft) => void;
}

const InlineFilterEditor = (props: InlineFilterEditor) => {
  const templates = props.filter?.type
    ? FilterEditorTemplate[props.filter.type as Filter.Type]?.({
        draft: props.filter,
        onDraftChange: props.onFilterChange,
        tableColumns: props.tableColumns,
      }) ?? []
    : [];

  return (
    <EditorWrapper
      selectedType={props.filter.type}
      templates={templates}
      selectorWidth={300}
    >
      <FilterTypeSelector
        selectedType={props.filter.type}
        onTypeChange={type => {
          if (type === Filter.Type.AndFilter) {
            props.onFilterChange({
              type,
              filter1: props.filter,
              filter2: { type: Filter.Type.FullPass },
            });
          } else {
            props.onFilterChange({ type });
          }
        }}
      />
    </EditorWrapper>
  );
};

export interface FilterEditorProps {
  tableColumns: TableColumns;
}

export const FilterEditor = ({ tableColumns }: FilterEditorProps) => {
  const [state, dispatch] = useFeatureDescriptorEditorContext();
  const filterDraft = state.filter as FilterDraft;

  return (
    <InlineFilterEditor
      tableColumns={tableColumns}
      filter={filterDraft}
      onFilterChange={value =>
        dispatch(
          FeatureDescriptorEditorActions.setDraftField({
            field: 'filter',
            value,
          })
        )
      }
    />
  );
};
